import {
  FREE_MODELS,
  MODEL_PROVIDERS,
  MODELS_TO_REMOVE,
  PREMIUM_MODELS,
} from "@/lib/constants/app";
import { create } from "zustand";
import { persist } from "zustand/middleware";
const STORAGE_KEY = "model-storage";

export const PROVIDER_CACHE_KEY = "provider-cache";
export const PROVIDER_CACHE_VERSION = "1.0.0";
export const DEFAULT_PROVIDER = "openai";
export const DEFAULT_MODEL = "gpt-4.1-mini";

// Re-export the imported constants for backward compatibility
export { FREE_MODELS, MODEL_PROVIDERS, MODELS_TO_REMOVE, PREMIUM_MODELS };

const INITIAL_STATE = {
  // General store state
  isLoading: false,
  error: null,
  initialized: true, // Set to true since we're using static models
  provider: DEFAULT_PROVIDER,
  model: DEFAULT_MODEL,
  availableModels: new Map(),
  providers: new Map(),
  pinnedModels: [], // User's pinned favorite models (for unauthenticated users)
  providerIds: [
    "anthropic",
    "openai",
    "google",
    "mistral",
    "deepseek",
    "groq",
    "qwen",
    "togetherai",
    "xai",
    "perplexity",
    "ollama",
    "cohere",
    "openrouter",
  ],
  availableProviders: [],

  activeProvider: null,
  selectedModel: null,

  providerInstances: {},
  currentProviderInstance: null,
  currentProvider: null,
  currentModel: null,
  response: null,
  isFullConfigOpen: false,
  selectedModelId: null,
  selectedProvider: null,
};

export const useModelStore = create(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,
      setIsLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      setProvider: (provider, isAutomatic = false) => {
        const currentProvider = get().provider;
        const currentModel = get().model;

        if (provider !== currentProvider) {
          // If this is an automatic update from setModel, don't reset the model
          // Only reset the model if this is a manual provider change
          if (isAutomatic) {
            set({ provider });
          } else {
            // Manual provider change - reset model to ensure compatibility
            set({
              provider,
              model: null,
            });
          }
        }
      },
      setModel: (model) => {
        // Automatically determine and set the provider based on the model
        const provider = MODEL_PROVIDERS[model] || "openai";
        const currentProvider = get().provider;

        // Set the model first
        set({ model });

        // Then update provider if needed (using isAutomatic flag)
        if (provider !== currentProvider) {
          get().setProvider(provider, true); // true = isAutomatic
        }
      },

      setSelectedModel: (model) => {
        set({ selectedModel: model });
      },
      updateProviderAvailability: (providers) => {
        const currentProviders = get().availableProviders;
        if (JSON.stringify(currentProviders) !== JSON.stringify(providers)) {
          set({ availableProviders: providers });
        }
      },
      toggleFullConfig: () =>
        set((state) => ({
          isFullConfigOpen: !state.isFullConfigOpen,
        })),
      initializeModelStore: async () => {
        // Initialize with static models instead of fetching
        get().initializeStaticModels();
      },
      initializeProviders: async () => {
        try {
          set({ isLoading: true });
          const cachedProviders = localStorage.getItem(PROVIDER_CACHE_KEY);
          if (cachedProviders) {
            const parsedProviders = JSON.parse(cachedProviders);
            if (parsedProviders.version === PROVIDER_CACHE_VERSION) {
              set({ providers: parsedProviders.providers });
            }
          }
        } catch (error) {
          set({ error: error.message });
        } finally {
          set({ isLoading: false });
        }
      },
      removeProvider: (providerName) => {
        const providers = get().providers;
        if (!providers.has(providerName)) {
          throw new Error("Provider not registered");
        }

        const updatedProviders = new Map(providers);
        updatedProviders.delete(providerName);

        set({
          providers: updatedProviders,
          activeProvider:
            updatedProviders.size > 0
              ? Array.from(updatedProviders.keys())[0]
              : null,
        });
      },
      initializeStaticModels: () => {
        // Create static model objects from the predefined lists
        const staticModels = [
          ...FREE_MODELS.map((modelId) => ({
            id: modelId,
            object: "model",
            created: Date.now(),
            owned_by: "openai",
            isPremium: false,
            isFree: true,
          })),
          ...PREMIUM_MODELS.map((modelId) => ({
            id: modelId,
            object: "model",
            created: Date.now(),
            owned_by: "openai",
            isPremium: true,
            isFree: false,
          })),
        ];

        set({
          availableModels: {
            ...get().availableModels,
            openai: staticModels,
          },
          initialized: true,
        });

        return {
          openai: staticModels,
          initialized: true,
        };
      },

      /* ---------------------------- */
      /* --------- Selectors -------- */
      /* ---------------------------- */
      getSelectedModel: () => get().model,
      getSelectedProvider: () => get().provider,
      getSelectedProviderModels: () => {
        const provider = get().provider;
        const availableModels = get().availableModels;
        return availableModels[provider] || [];
      },
      getSelectedProviderModel: (modelId) => {
        const provider = get().providers.get(get().provider);
        return (
          provider?.chatModels?.find((model) => model.id === modelId) || null
        );
      },
      getModelFunctionCallAbility: (modelId) => {
        const model = get().getSelectedProviderModel(modelId);
        return model?.functionCall || false;
      },
      getModelMaxOutputTokens: (modelId) => {
        const model = get().getSelectedProviderModel(modelId);
        return model?.maxOutput || 4096;
      },
      getModelContextLength: (modelId) => {
        const model = get().getSelectedProviderModel(modelId);
        return model?.tokens || 4096;
      },
      getModelInputPrice: (modelId) => {
        const model = get().getSelectedProviderModel(modelId);
        return model?.pricing?.input || 0;
      },
      getModelOutputPrice: (modelId) => {
        const model = get().getSelectedProviderModel(modelId);
        return model?.pricing?.output || 0;
      },
      getModelById: (provider, modelId) => {
        if (!provider || !modelId) return null;

        // Try to get from availableModels
        const availableModels = get().availableModels;
        const providerModels = availableModels[provider] || [];
        return providerModels.find((model) => model.id === modelId) || null;
      },

      reset: () =>
        set({
          provider: DEFAULT_PROVIDER,
          model: DEFAULT_MODEL,
          selectedModel: null,
          isLoading: false,
          error: null,
          pinnedModels: [],
        }),

      // Pinned models management (for unauthenticated users)
      getPinnedModels: () => get().pinnedModels,

      togglePinModel: (modelId) => {
        const current = get().pinnedModels;
        const isPinned = current.includes(modelId);

        if (isPinned) {
          set({ pinnedModels: current.filter((id) => id !== modelId) });
        } else {
          set({ pinnedModels: [...current, modelId] });
        }
      },

      setPinnedModels: (models) => set({ pinnedModels: models }),

      setAvailableModels: (models) =>
        set((state) => ({
          availableModels: {
            ...state.availableModels,
            ...models,
          },
        })),

      // Helper to get provider for a model
      getProviderForModel: (modelId) => {
        return MODEL_PROVIDERS[modelId] || "openai";
      },

      // Get the effective provider (based on current model if available)
      getEffectiveProvider: () => {
        const state = get();
        const currentModel = state.model;

        if (currentModel) {
          // Return provider based on current model
          return MODEL_PROVIDERS[currentModel] || "openai";
        }

        // Fallback to stored provider
        return state.provider || DEFAULT_PROVIDER;
      },

      // Get current model for the active provider
      getCurrentModel: () => {
        const { currentProvider, currentModel, models } = get();
        if (!currentModel || !currentProvider) return null;

        const providerModels = models[currentProvider]?.chatModels || [];
        return (
          providerModels.find((model) => model.id === currentModel) || null
        );
      },

      // Get default model for token pricing calculations
      getDefaultModel: () => {
        const state = get();
        const currentModel = state.model;
        const currentProvider = state.provider || DEFAULT_PROVIDER;

        // First try to get the current model if it exists
        if (currentModel) {
          const modelData = state.getModelById(currentProvider, currentModel);
          if (modelData) return modelData;
        }

        // If no current model, return a default based on provider
        const defaultModelId =
          currentProvider === "mistral"
            ? "mistral-large-latest"
            : currentProvider === "deepseek"
              ? "deepseek-chat"
              : currentProvider === "anthropic"
                ? "claude-opus-4-20250514"
                : currentProvider === "google"
                  ? "gemini-pro"
                  : "gpt-4.1-mini";

        // Try to get model data for the default model ID
        const defaultModel = state.getModelById(
          currentProvider,
          defaultModelId
        );
        if (defaultModel) return defaultModel;

        // Last resort fallback - return an object with basic pricing info
        return {
          id: defaultModelId,
          displayName: "Default Model",
          pricing: {
            input:
              currentProvider === "mistral"
                ? 2
                : currentProvider === "deepseek"
                  ? 2
                  : currentProvider === "anthropic"
                    ? 15
                    : 2.5,
            output:
              currentProvider === "mistral"
                ? 6
                : currentProvider === "deepseek"
                  ? 8
                  : currentProvider === "anthropic"
                    ? 75
                    : 10,
          },
          tokens: 128000,
        };
      },

      // Set current provider
      setCurrentProvider: (provider) => {
        if (!provider || !MODEL_PROVIDERS.includes(provider)) return;
        set({ currentProvider: provider });
      },

      // Set current model
      setCurrentModel: (modelId) => {
        if (!modelId) return;
        set({ currentModel: modelId });
      },

      // Update model availability state
      updateModelAvailability: (modelAvailability) => {
        set({ availableModels: modelAvailability });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        availableModels: state.availableModels,
        selectedModel: state.selectedModel,
        initialized: state.initialized,
        activeProvider: state.activeProvider,
        selectedModelId: state.selectedModelId,
        selectedProvider: state.selectedProvider,
        providerIds: state.providerIds,
        provider: state.provider,
        model: state.model,
        pinnedModels: state.pinnedModels,
      }),
    }
  )
);

export default useModelStore;
